import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import C from '../../data/colors';
import KPIIntegrityIndicator from './KPIIntegrityIndicator';
import { Mail, MessageSquare, Send, Eye, ChevronLeft, ChevronRight, Edit3, AlertTriangle, Check, Code, Type } from 'lucide-react';
import { isCommunicationsHubEnabled } from './communications/communicationsHubFlag';
import AdminPageHeader from './AdminPageHeader';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper: Deduplicate emails (case-insensitive)
const deduplicateEmails = (emailArray) => {
  const seen = new Set();
  return emailArray.filter(e => {
    const norm = e.trim().toLowerCase();
    if (!norm || seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
};

const SendNoticesTab = ({ selectedIds = [], guests = [], refresh, integrityCheck = null }) => {
  const communicationsHubEnabled = isCommunicationsHubEnabled();
  const [mode, setMode] = useState('email'); // email | sms
  const [templates, setTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState(selectedIds);
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewCorrelationId, setPreviewCorrelationId] = useState(null);
  const [previewValidFor, setPreviewValidFor] = useState(null); // Tracks what was previewed
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [autoReplyTemplate, setAutoReplyTemplate] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [editBodyMode, setEditBodyMode] = useState('visual'); // visual | source

  // Editable header fields for preview
  const [editedFromName, setEditedFromName] = useState('');
  const [editedFromEmail, setEditedFromEmail] = useState('');
  const [editedToEmail, setEditedToEmail] = useState('');
  const [editedCc, setEditedCc] = useState('');
  const [editedBcc, setEditedBcc] = useState('');

  // WYSIWYG editor ref
  const wysiwygRef = useRef(null);

  // Bulk send confirmation
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState('');

  // Quick template preview modal
  const [showQuickPreview, setShowQuickPreview] = useState(false);
  const [quickPreviewContent, setQuickPreviewContent] = useState({ subject: '', body: '', name: '' });

  useEffect(() => {
    loadTemplates();
    loadSettings();
  }, []);

  useEffect(() => {
    setSelectedGuests(selectedIds);
  }, [selectedIds]);

  const loadTemplates = async () => {
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/api/email-templates`),
        axios.get(`${API}/api/sms-templates`)
      ]);
      setTemplates(emailRes.data);
      setSmsTemplates(smsRes.data);
      if (emailRes.data.length > 0 && !selectedTemplate) {
        setSelectedTemplate(emailRes.data[0].id);
      }
    } catch (e) {
      console.error('Error loading templates:', e);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/settings`);
      setSettings(res.data);
      // Pre-fill BCC from settings
      if (res.data?.bcc_email) {
        setBccEmails(res.data.bcc_email);
      }
    } catch (e) {
      console.error('Error loading settings:', e);
    }
  };

  const loadAutoReplyTemplate = async (firstName = 'Guest') => {
    try {
      const res = await axios.get(`${API}/api/email-templates/auto-reply-preview?first_name=${encodeURIComponent(firstName)}`);
      setAutoReplyTemplate(res.data);
      return res.data;
    } catch (e) {
      console.error('Error loading auto-reply template:', e);
      return null;
    }
  };

  const getTemplate = () => {
    if (mode === 'email') {
      return templates.find(t => t.id === selectedTemplate);
    }
    return smsTemplates.find(t => t.id === selectedTemplate);
  };

  const getSelectedGuestObjects = () => {
    return guests.filter(g => selectedGuests.includes(g.id));
  };

  // Check if current selections match what was previewed
  const isPreviewValid = () => {
    if (!previewValidFor || !previewCorrelationId) return false;
    
    const currentHash = JSON.stringify({
      templateId: selectedTemplate,
      mode,
      guestIds: [...selectedGuests].sort(),
      cc: ccEmails,
      bcc: bccEmails
    });
    
    return previewValidFor === currentHash;
  };

  // Get deduplicated BCC list (single source of truth from settings)
  const getDeduplicatedBcc = () => {
    const systemBcc = settings?.bcc_email || '';
    const userBcc = bccEmails || '';
    
    // Combine and deduplicate
    const allBcc = [...systemBcc.split(','), ...userBcc.split(',')]
      .map(e => e.trim())
      .filter(Boolean);
    
    return deduplicateEmails(allBcc).join(', ');
  };

  const openPreview = async () => {
    const template = getTemplate();
    if (!template) {
      setMessage({ type: 'error', text: 'Please select a template' });
      return;
    }

    if (selectedGuests.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one recipient' });
      return;
    }

    // For Registration Confirmation, load the live auto-reply
    const guestList = getSelectedGuestObjects();
    let previewTemplate = template;
    
    if (template.template_type === 'confirmation' || template.name?.includes('Registration Confirmation')) {
      const firstName = guestList[0]?.first_name || 'Guest';
      const autoReply = await loadAutoReplyTemplate(firstName);
      if (autoReply) {
        previewTemplate = autoReply;
      }
    }

    // Initialize editable header fields
    setEditedFromName(settings?.sender_name || 'The Valenciano Family');
    setEditedFromEmail(settings?.sender_email || 'Event@80yearsofconnection.com');
    setEditedToEmail(guestList[0]?.email || '');
    setEditedCc(ccEmails);
    // FIX #1: Use deduplicated BCC - single source only
    setEditedBcc(getDeduplicatedBcc());

    // Log preview opened
    try {
      const res = await axios.post(`${API}/api/preview/log-opened`, {
        template_id: template.id,
        template_name: template.name,
        channel: mode,
        recipient_count: selectedGuests.length,
        recipient_ids: selectedGuests,
        cc_emails: ccEmails.split(',').map(e => e.trim()).filter(Boolean),
        bcc_emails: deduplicateEmails(bccEmails.split(',').map(e => e.trim()).filter(Boolean))
      });
      setPreviewCorrelationId(res.data.correlation_id);
    } catch (e) {
      console.error('Error logging preview:', e);
    }

    // Store what was previewed
    const currentHash = JSON.stringify({
      templateId: selectedTemplate,
      mode,
      guestIds: [...selectedGuests].sort(),
      cc: ccEmails,
      bcc: bccEmails
    });
    setPreviewValidFor(currentHash);

    // Set up preview state
    setEditedSubject(previewTemplate.subject || '');
    setEditedBody(previewTemplate.html_content || previewTemplate.message_text || '');
    setPreviewIndex(0);
    setIsEditingBody(false);
    setEditBodyMode('visual');
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setIsEditingBody(false);
    setEditBodyMode('visual');
  };

  // Log header field edits
  const logHeaderEdit = async (field, oldVal, newVal) => {
    if (previewCorrelationId && oldVal !== newVal) {
      try {
        await axios.post(`${API}/api/preview/log-edit`, {
          correlation_id: previewCorrelationId,
          field: `header_${field}`,
          before: oldVal,
          after: newVal
        });
      } catch (e) {
        console.error('Error logging header edit:', e);
      }
    }
  };

  const handleSubjectEdit = async (newSubject) => {
    const oldSubject = editedSubject;
    setEditedSubject(newSubject);
    
    // Log edit
    if (previewCorrelationId && oldSubject !== newSubject) {
      try {
        await axios.post(`${API}/api/preview/log-edit`, {
          correlation_id: previewCorrelationId,
          field: 'subject',
          before: oldSubject,
          after: newSubject
        });
      } catch (e) {
        console.error('Error logging edit:', e);
      }
    }
  };

  const handleBodyEdit = async (newBody) => {
    const oldBody = editedBody;
    setEditedBody(newBody);
    
    // Log edit (only log once when done editing, not on every keystroke)
    if (previewCorrelationId && oldBody !== newBody) {
      try {
        await axios.post(`${API}/api/preview/log-edit`, {
          correlation_id: previewCorrelationId,
          field: 'body',
          before: oldBody.substring(0, 100) + '...', // Truncate for logging
          after: newBody.substring(0, 100) + '...'
        });
      } catch (e) {
        console.error('Error logging edit:', e);
      }
    }
  };

  const handleSend = async () => {
    if (!isPreviewValid()) {
      setMessage({ type: 'error', text: 'Please preview your message before sending. Selections may have changed.' });
      return;
    }

    // Check bulk threshold
    if (selectedGuests.length > 5) {
      setShowBulkConfirm(true);
      return;
    }

    await executeSend();
  };

  const executeSend = async () => {
    setSending(true);
    setMessage(null);
    
    try {
      const template = getTemplate();
      const originalBody = template?.html_content || template?.message_text || '';
      const bodyWasEdited = editedBody !== originalBody;
      
      // FIX #1: Deduplicate BCC before sending
      const finalBccList = deduplicateEmails(editedBcc.split(',').map(e => e.trim()).filter(Boolean));
      const finalCcList = deduplicateEmails(editedCc.split(',').map(e => e.trim()).filter(Boolean));
      
      if (mode === 'email') {
        const response = await axios.post(`${API}/api/emails/send?template_id=${selectedTemplate}`, {
          guest_ids: selectedGuests,
          cc_emails: finalCcList,
          bcc_emails: finalBccList,
          confirm_bulk_send: true,
          correlation_id: previewCorrelationId,
          custom_subject: editedSubject !== template?.subject ? editedSubject : null,
          custom_body: bodyWasEdited ? editedBody : null,
          // Pass edited header fields
          custom_from_name: editedFromName !== (settings?.sender_name || 'The Valenciano Family') ? editedFromName : null,
          custom_from_email: editedFromEmail !== (settings?.sender_email || 'Event@80yearsofconnection.com') ? editedFromEmail : null
        });
        
        // FIX #4: Check response for actual send status
        const sendResult = response.data;
        if (sendResult.status === 'queued') {
          setMessage({ type: 'success', text: `Email queued for Outbox review. No guests were contacted. ${sendResult.queued_count || 0} recipient(s) pending.` });
        } else if (sendResult.mode === 'test') {
          setMessage({ type: 'success', text: `Test email sent to ${sendResult.sent_count || 0} configured test recipient(s). Guest statuses were not updated.` });
        } else if (sendResult.status === 'success' || sendResult.sent_count > 0) {
          setMessage({ type: 'success', text: `Email sent to ${sendResult.sent_count || selectedGuests.length} recipient(s)` });
        } else if (sendResult.failed_count > 0) {
          setMessage({ type: 'error', text: `${sendResult.failed_count} email(s) failed to send. Check Sent History.` });
        } else {
          setMessage({ type: 'success', text: `Email sent to ${selectedGuests.length} recipient(s)` });
        }
      } else {
        const response = await axios.post(`${API}/api/sms/send-to-guests`, {
          guest_ids: selectedGuests,
          template_id: selectedTemplate,
          custom_body: bodyWasEdited ? editedBody : null
        });
        const sendResult = response.data;
        if (sendResult.status === 'queued') {
          setMessage({ type: 'success', text: `SMS queued for Outbox review. No guests were contacted. ${sendResult.queued_count || 0} recipient(s) pending.` });
        } else {
          setMessage({ type: 'success', text: `SMS sent to ${selectedGuests.length} recipient(s)` });
        }
      }

      // Log send confirmed
      if (previewCorrelationId) {
        try {
          await axios.post(`${API}/api/interactions/log/send-confirmed`, {
            correlation_id: previewCorrelationId,
            channel: mode,
            recipient_count: selectedGuests.length
          });
        } catch (e) {
          console.error('Error logging send confirmed:', e);
        }
      }
      
      // Clear state
      setSelectedGuests([]);
      setPreviewCorrelationId(null);
      setPreviewValidFor(null);
      setShowBulkConfirm(false);
      setBulkConfirmText('');
      setIsEditingBody(false);
      setEditBodyMode('visual');
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error sending';
      setMessage({ type: 'error', text: detail });
    }
    
    setSending(false);
  };

  const toggleGuest = (id) => {
    setSelectedGuests(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    // Invalidate preview when selection changes
    setPreviewValidFor(null);
  };

  const currentTemplates = mode === 'email' ? templates : smsTemplates;
  const guestList = getSelectedGuestObjects();
  const previewGuest = guestList[previewIndex] || guestList[0];

  // Personalize preview content
  const getPersonalizedHtml = () => {
    let html = editedBody;
    if (previewGuest) {
      html = html.replace(/\{\{first_name\}\}/g, previewGuest.first_name || '');
      html = html.replace(/\{\{last_name\}\}/g, previewGuest.last_name || '');
      html = html.replace(/\{\{full_name\}\}/g, `${previewGuest.first_name || ''} ${previewGuest.last_name || ''}`.trim());
      html = html.replace(/\{\{email\}\}/g, previewGuest.email || '');
      // Handle the auto-reply template format
      html = html.replace(/\{\{ insert first_name \}\}/g, previewGuest.first_name ? ` ${previewGuest.first_name}` : '');
    }
    return html;
  };

  return (
    <div data-testid="send-notices-tab">
      <AdminPageHeader
        eyebrow="Messaging"
        title="Send Notices"
        tagline="Compose and send email or SMS notices to your guests."
      />

      {/* KPI integrity warning — renders only when integrity fails (send-critical surface). */}
      <KPIIntegrityIndicator integrityCheck={integrityCheck} variant="inline-warning" context="notices" />
      {communicationsHubEnabled && (
        <div style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          padding: '12px 14px',
          borderRadius: 10,
          border: `1px solid ${C.gold}`,
          background: C.goldMuted,
          color: C.textPrimary,
          marginBottom: 16,
          fontSize: 13
        }}>
          <AlertTriangle size={18} color={C.gold} style={{ flex: '0 0 auto', marginTop: 1 }} />
          <div>
            <strong style={{ display: 'block', marginBottom: 3 }}>Communications Hub is the preferred send workflow.</strong>
            <span>Use Send Notices only as a fallback. Preview first, avoid duplicate sends, and check Outbox when a message is queued for review.</span>
          </div>
        </div>
      )}
      {/* Mode Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          data-testid="mode-email"
          onClick={() => { setMode('email'); setPreviewValidFor(null); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 8,
            border: mode === 'email' ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
            background: mode === 'email' ? C.goldMuted : C.bgCard,
            color: C.textPrimary, cursor: 'pointer', fontFamily: 'Cinzel, serif'
          }}
        >
          <Mail size={18} /> Email
        </button>
        <button
          data-testid="mode-sms"
          onClick={() => { setMode('sms'); setPreviewValidFor(null); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 8,
            border: mode === 'sms' ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
            background: mode === 'sms' ? C.goldMuted : C.bgCard,
            color: C.textPrimary, cursor: 'pointer', fontFamily: 'Cinzel, serif'
          }}
        >
          <MessageSquare size={18} /> SMS
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          background: message.type === 'success' ? C.successBg : C.errorBg,
          color: message.type === 'success' ? C.success : C.error,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left: Template Selection */}
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 12, fontFamily: 'Cinzel, serif' }}>
            SELECT TEMPLATE
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {currentTemplates.map(t => (
              <label
                key={t.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 8,
                  border: selectedTemplate === t.id ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
                  background: selectedTemplate === t.id ? C.goldMuted : C.bgCard,
                  cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                <input
                  type="radio"
                  name="template"
                  checked={selectedTemplate === t.id}
                  onChange={() => { setSelectedTemplate(t.id); setPreviewValidFor(null); }}
                  style={{ accentColor: C.gold }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: C.textPrimary, fontSize: 14 }}>{t.name}</div>
                  {mode === 'email' && t.subject && (
                    <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>
                      Subject: {t.subject}
                    </div>
                  )}
                </div>
                {t.is_active && (
                  <span style={{ fontSize: 10, padding: '2px 6px', background: C.gold, color: '#000', borderRadius: 4 }}>
                    ACTIVE
                  </span>
                )}
              </label>
            ))}
          </div>

          {/* Quick Preview Button */}
          {selectedTemplate && (
            <button
              data-testid="quick-preview-btn"
              onClick={async () => {
                const t = getTemplate();
                if (t) {
                  // For Registration Confirmation, load the live auto-reply
                  if (mode === 'email' && (t.template_type === 'confirmation' || t.name?.includes('Registration Confirmation'))) {
                    const autoReply = await loadAutoReplyTemplate('Guest');
                    if (autoReply) {
                      setQuickPreviewContent({
                        name: autoReply.name,
                        subject: autoReply.subject,
                        body: autoReply.html_content
                      });
                    } else {
                      setQuickPreviewContent({
                        name: t.name,
                        subject: t.subject || '',
                        body: t.html_content || t.message_text || ''
                      });
                    }
                  } else {
                    setQuickPreviewContent({
                      name: t.name,
                      subject: t.subject || '',
                      body: t.html_content || t.message_text || ''
                    });
                  }
                  setShowQuickPreview(true);
                }
              }}
              style={{
                marginTop: 12, display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 6, border: `1px solid ${C.gold}`,
                background: 'transparent', color: C.gold, cursor: 'pointer', fontSize: 13
              }}
            >
              <Eye size={14} /> Quick Template View
            </button>
          )}
        </div>

        {/* Right: Recipients & CC/BCC */}
        <div>
          {/* CC/BCC Fields - Above Recipients */}
          {mode === 'email' && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 12, fontFamily: 'Cinzel, serif' }}>
                CC / BCC
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <label style={{ fontSize: 12, color: C.textSecondary, display: 'block', marginBottom: 4 }}>CC (comma-separated)</label>
                  <input
                    data-testid="cc-input"
                    value={ccEmails}
                    onChange={e => { setCcEmails(e.target.value); setPreviewValidFor(null); }}
                    placeholder="email1@example.com, email2@example.com"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 6,
                      border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box',
                      background: C.bgInput, color: C.textPrimary
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: C.textSecondary, display: 'block', marginBottom: 4 }}>BCC (comma-separated)</label>
                  <input
                    data-testid="bcc-input"
                    value={bccEmails}
                    onChange={e => { setBccEmails(e.target.value); setPreviewValidFor(null); }}
                    placeholder="Default: Settings BCC"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 6,
                      border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box',
                      background: C.bgInput, color: C.textPrimary
                    }}
                  />
                  <div style={{ fontSize: 10, color: C.textSecondary, marginTop: 4 }}>
                    System BCC ({settings?.bcc_email || 'not set'}) is always included
                  </div>
                </div>
              </div>
            </div>
          )}

          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 12, fontFamily: 'Cinzel, serif' }}>
            SELECT RECIPIENTS ({selectedGuests.length})
          </h3>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8, background: C.bgCard }}>
            {guests.filter(g => !g.is_deleted).map(g => (
              <label
                key={g.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderBottom: `1px solid ${C.border}`,
                  cursor: 'pointer', background: selectedGuests.includes(g.id) ? C.goldMuted : 'transparent'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedGuests.includes(g.id)}
                  onChange={() => toggleGuest(g.id)}
                  style={{ accentColor: C.gold }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: C.textPrimary }}>{g.first_name} {g.last_name}</div>
                  <div style={{ fontSize: 11, color: C.textSecondary }}>{g.email || g.phone || 'No contact'}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        marginTop: 24,
        padding: '16px 0',
        borderTop: `1px solid ${C.border}`,
        display: 'flex',
        gap: 12,
        alignItems: 'center'
      }}>
        <button
          data-testid="preview-btn"
          onClick={openPreview}
          disabled={!selectedTemplate || selectedGuests.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 8,
            border: `1px solid ${C.gold}`,
            background: 'transparent', color: C.gold,
            cursor: selectedTemplate && selectedGuests.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: 14, fontFamily: 'Cinzel, serif',
            opacity: selectedTemplate && selectedGuests.length > 0 ? 1 : 0.5
          }}
        >
          <Eye size={18} /> Preview
        </button>

        <button
          data-testid="send-btn"
          onClick={handleSend}
          disabled={!isPreviewValid() || sending}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 8,
            border: 'none',
            background: isPreviewValid() ? C.gold : C.bgPrimary,
            color: isPreviewValid() ? '#000' : C.textSecondary,
            cursor: isPreviewValid() && !sending ? 'pointer' : 'not-allowed',
            fontSize: 14, fontFamily: 'Cinzel, serif'
          }}
        >
          <Send size={18} /> {sending ? 'Sending...' : 'Send'}
        </button>

        {!isPreviewValid() && selectedGuests.length > 0 && (
          <span style={{ fontSize: 12, color: C.warning, display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertTriangle size={14} /> Preview required before sending
          </span>
        )}

        {isPreviewValid() && (
          <span style={{ fontSize: 12, color: C.success, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Check size={14} /> Preview validated
          </span>
        )}
      </div>

      {/* Full Preview Modal */}
      {showPreview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: C.bgCard, borderRadius: 12, width: '90%', maxWidth: 800,
            maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            border: `1px solid ${C.border}`
          }}>
            {/* Preview Header */}
            <div style={{
              padding: '16px 20px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontFamily: 'Cinzel, serif', color: C.textPrimary }}>
                Delivery Preview
              </h3>
              <button
                onClick={closePreview}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: C.textSecondary }}
              >
                ×
              </button>
            </div>

            {/* FIX #2: Editable Preview Meta Fields */}
            <div style={{ padding: '12px 20px', background: C.bgPrimary, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 13, alignItems: 'center' }}>
                {/* From Name */}
                <span style={{ color: C.textSecondary }}>From Name:</span>
                <input
                  data-testid="preview-from-name"
                  value={editedFromName}
                  onChange={e => setEditedFromName(e.target.value)}
                  onBlur={e => logHeaderEdit('from_name', settings?.sender_name || 'The Valenciano Family', e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: 4,
                    border: `1px solid ${C.border}`, fontSize: 13, width: '100%', boxSizing: 'border-box',
                    background: C.bgInput, color: C.textPrimary
                  }}
                />
                
                {/* From Email */}
                <span style={{ color: C.textSecondary }}>From Email:</span>
                <input
                  data-testid="preview-from-email"
                  value={editedFromEmail}
                  onChange={e => setEditedFromEmail(e.target.value)}
                  onBlur={e => logHeaderEdit('from_email', settings?.sender_email || 'Event@80yearsofconnection.com', e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: 4,
                    border: `1px solid ${C.border}`, fontSize: 13, width: '100%', boxSizing: 'border-box',
                    background: C.bgInput, color: C.textPrimary
                  }}
                />
                
                {/* To */}
                <span style={{ color: C.textSecondary }}>To:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    data-testid="preview-to-email"
                    value={editedToEmail}
                    onChange={e => setEditedToEmail(e.target.value)}
                    onBlur={e => logHeaderEdit('to', previewGuest?.email || '', e.target.value)}
                    style={{
                      flex: 1, padding: '4px 8px', borderRadius: 4,
                      border: `1px solid ${C.border}`, fontSize: 13,
                      background: C.bgInput, color: C.textPrimary
                    }}
                  />
                  {guestList.length > 1 && (
                    <span style={{ fontSize: 11, color: C.textSecondary, whiteSpace: 'nowrap' }}>
                      +{guestList.length - 1} more
                    </span>
                  )}
                </div>

                {/* CC */}
                <span style={{ color: C.textSecondary }}>CC:</span>
                <input
                  data-testid="preview-cc"
                  value={editedCc}
                  onChange={e => setEditedCc(e.target.value)}
                  onBlur={e => logHeaderEdit('cc', ccEmails, e.target.value)}
                  placeholder="Optional"
                  style={{
                    padding: '4px 8px', borderRadius: 4,
                    border: `1px solid ${C.border}`, fontSize: 13, width: '100%', boxSizing: 'border-box',
                    background: C.bgInput, color: C.textPrimary
                  }}
                />

                {/* BCC - FIX #1: Show single deduplicated value */}
                <span style={{ color: C.textSecondary }}>BCC:</span>
                <input
                  data-testid="preview-bcc"
                  value={editedBcc}
                  onChange={e => setEditedBcc(e.target.value)}
                  onBlur={e => logHeaderEdit('bcc', getDeduplicatedBcc(), e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: 4,
                    border: `1px solid ${C.border}`, fontSize: 13, width: '100%', boxSizing: 'border-box',
                    background: C.bgInput, color: C.textPrimary
                  }}
                />

                {mode === 'email' && (
                  <>
                    <span style={{ color: C.textSecondary }}>Subject:</span>
                    <input
                      data-testid="preview-subject-input"
                      value={editedSubject}
                      onChange={e => handleSubjectEdit(e.target.value)}
                      style={{
                        padding: '4px 8px', borderRadius: 4,
                        border: `1px solid ${C.border}`, fontSize: 13, width: '100%', boxSizing: 'border-box',
                        background: C.bgInput, color: C.textPrimary
                      }}
                    />
                  </>
                )}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: C.warning, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Edit3 size={12} />
                All fields above are editable for this send only
              </div>
            </div>

            {/* Recipient Navigation */}
            {guestList.length > 1 && (
              <div style={{
                padding: '8px 20px', background: C.goldMuted,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16
              }}>
                <button
                  data-testid="preview-prev-recipient"
                  onClick={() => {
                    const newIndex = Math.max(0, previewIndex - 1);
                    setPreviewIndex(newIndex);
                    setEditedToEmail(guestList[newIndex]?.email || '');
                  }}
                  disabled={previewIndex === 0}
                  style={{
                    background: 'none', border: 'none', cursor: previewIndex === 0 ? 'default' : 'pointer',
                    opacity: previewIndex === 0 ? 0.3 : 1
                  }}
                >
                  <ChevronLeft size={20} color={C.gold} />
                </button>
                <span style={{ fontSize: 13, color: C.textPrimary }}>
                  Recipient {previewIndex + 1} of {guestList.length}:
                  <strong style={{ marginLeft: 6 }}>{previewGuest?.first_name} {previewGuest?.last_name}</strong>
                </span>
                <button
                  data-testid="preview-next-recipient"
                  onClick={() => {
                    const newIndex = Math.min(guestList.length - 1, previewIndex + 1);
                    setPreviewIndex(newIndex);
                    setEditedToEmail(guestList[newIndex]?.email || '');
                  }}
                  disabled={previewIndex === guestList.length - 1}
                  style={{
                    background: 'none', border: 'none', cursor: previewIndex === guestList.length - 1 ? 'default' : 'pointer',
                    opacity: previewIndex === guestList.length - 1 ? 0.3 : 1
                  }}
                >
                  <ChevronRight size={20} color={C.gold} />
                </button>
              </div>
            )}

            {/* Desktop/Mobile Toggle + Edit Body Toggle */}
            <div style={{
              padding: '8px 20px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              {/* Preview Size Toggle */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  data-testid="preview-desktop-toggle"
                  onClick={() => setPreviewMode('desktop')}
                  style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 12,
                    border: previewMode === 'desktop' ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
                    background: previewMode === 'desktop' ? C.goldMuted : C.bgCard,
                    color: previewMode === 'desktop' ? C.gold : C.textPrimary,
                    cursor: 'pointer', fontWeight: previewMode === 'desktop' ? 600 : 400
                  }}
                >
                  Desktop
                </button>
                <button
                  data-testid="preview-mobile-toggle"
                  onClick={() => setPreviewMode('mobile')}
                  style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 12,
                    border: previewMode === 'mobile' ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
                    background: previewMode === 'mobile' ? C.goldMuted : C.bgCard,
                    color: previewMode === 'mobile' ? C.gold : C.textPrimary,
                    cursor: 'pointer', fontWeight: previewMode === 'mobile' ? 600 : 400
                  }}
                >
                  Mobile
                </button>
              </div>

              {/* Edit Body Toggle with Visual/Source sub-toggle */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isEditingBody && mode === 'email' && (
                  <div style={{ display: 'flex', gap: 2, background: C.bgPrimary, borderRadius: 6, padding: 2 }}>
                    <button
                      data-testid="edit-mode-visual"
                      onClick={() => {
                        // Sync from source if switching from source mode
                        if (editBodyMode === 'source' && wysiwygRef.current) {
                          setEditedBody(wysiwygRef.current.innerHTML);
                        }
                        setEditBodyMode('visual');
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 8px', borderRadius: 4, fontSize: 11,
                        border: 'none',
                        background: editBodyMode === 'visual' ? C.bgCard : 'transparent',
                        color: editBodyMode === 'visual' ? C.textPrimary : C.textSecondary,
                        cursor: 'pointer', boxShadow: editBodyMode === 'visual' ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'
                      }}
                    >
                      <Type size={12} /> Visual
                    </button>
                    <button
                      data-testid="edit-mode-source"
                      onClick={() => {
                        // Sync from visual if switching to source mode
                        if (editBodyMode === 'visual' && wysiwygRef.current) {
                          setEditedBody(wysiwygRef.current.innerHTML);
                        }
                        setEditBodyMode('source');
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 8px', borderRadius: 4, fontSize: 11,
                        border: 'none',
                        background: editBodyMode === 'source' ? C.bgCard : 'transparent',
                        color: editBodyMode === 'source' ? C.textPrimary : C.textSecondary,
                        cursor: 'pointer', boxShadow: editBodyMode === 'source' ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'
                      }}
                    >
                      <Code size={12} /> Source
                    </button>
                  </div>
                )}
                <button
                  data-testid="toggle-edit-body"
                  onClick={() => {
                    if (isEditingBody && wysiwygRef.current) {
                      // Save changes when closing edit mode
                      setEditedBody(wysiwygRef.current.innerHTML);
                      handleBodyEdit(wysiwygRef.current.innerHTML);
                    }
                    setIsEditingBody(!isEditingBody);
                    if (!isEditingBody) {
                      setEditBodyMode('visual'); // Default to visual when opening
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 6, fontSize: 12,
                    border: isEditingBody ? `2px solid ${C.warning}` : `1px solid ${C.border}`,
                    background: isEditingBody ? C.warningBg : C.bgCard,
                    color: isEditingBody ? C.warning : C.textPrimary,
                    cursor: 'pointer'
                  }}
                >
                  <Edit3 size={14} />
                  {isEditingBody ? 'Done Editing' : 'Edit Body'}
                </button>
              </div>
            </div>

            {/* Preview Content - Email body display (white bg for actual email preview is intentional) */}
            <div style={{ flex: 1, overflow: 'auto', padding: 20, background: C.bgPrimary }}>
              {mode === 'email' ? (
                <div style={{
                  maxWidth: previewMode === 'mobile' ? 375 : '100%',
                  margin: '0 auto',
                  transition: 'max-width 0.3s ease'
                }}>
                  {isEditingBody ? (
                    <div>
                      <div style={{ fontSize: 12, color: C.warning, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <AlertTriangle size={14} />
                        {editBodyMode === 'visual' 
                          ? 'Visual editing mode. Click text to edit. Changes apply to this send only.'
                          : 'Source code editing. Changes apply to this send only.'}
                      </div>
                      {editBodyMode === 'visual' ? (
                        /* WYSIWYG Visual Editor */
                        <div
                          ref={wysiwygRef}
                          data-testid="wysiwyg-editor"
                          contentEditable
                          suppressContentEditableWarning
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(getPersonalizedHtml()) }}
                          onBlur={() => {
                            if (wysiwygRef.current) {
                              const newHtml = wysiwygRef.current.innerHTML;
                              setEditedBody(newHtml);
                              handleBodyEdit(newHtml);
                            }
                          }}
                          style={{
                            width: '100%', minHeight: 400, padding: 16, borderRadius: 8,
                            border: `2px solid ${C.warning}`, fontSize: 14,
                            boxSizing: 'border-box', background: C.bgCard, color: '#000000',
                            outline: 'none', cursor: 'text',
                            overflowY: 'auto'
                          }}
                        />
                      ) : (
                        /* Source Code Editor */
                        <textarea
                          data-testid="edit-body-textarea"
                          value={editedBody}
                          onChange={e => setEditedBody(e.target.value)}
                          onBlur={() => handleBodyEdit(editedBody)}
                          style={{
                            width: '100%', minHeight: 400, padding: 12, borderRadius: 8,
                            border: `2px solid ${C.warning}`, fontSize: 12, fontFamily: 'monospace',
                            boxSizing: 'border-box', resize: 'vertical',
                            background: C.bgCard, color: C.textPrimary
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div style={{
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      background: C.bgCard, minHeight: 400,
                      boxShadow: previewMode === 'mobile' ? '0 4px 20px rgba(0,0,0,0.3)' : 'none'
                    }}>
                      <iframe
                        title="Email Preview"
                        srcDoc={getPersonalizedHtml()}
                        style={{ width: '100%', height: previewMode === 'mobile' ? 600 : 500, border: 'none' }}
                      />
                    </div>
                  )}
                  {previewMode === 'mobile' && !isEditingBody && (
                    <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: C.textSecondary }}>
                      Mobile Preview (375px width)
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  maxWidth: 320, margin: '0 auto', padding: 20,
                  background: C.bgCard, borderRadius: 20, border: `1px solid ${C.border}`
                }}>
                  {isEditingBody ? (
                    <div>
                      <div style={{ fontSize: 12, color: C.warning, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <AlertTriangle size={14} />
                        Editing SMS text. Changes apply to this send only.
                      </div>
                      <textarea
                        data-testid="edit-sms-textarea"
                        value={editedBody}
                        onChange={e => setEditedBody(e.target.value)}
                        onBlur={() => handleBodyEdit(editedBody)}
                        style={{
                          width: '100%', minHeight: 150, padding: 12, borderRadius: 8,
                          border: `2px solid ${C.warning}`, fontSize: 14,
                          boxSizing: 'border-box', resize: 'vertical',
                          background: C.bgInput, color: C.textPrimary
                        }}
                        maxLength={1600}
                      />
                      <div style={{ fontSize: 11, color: C.textSecondary, marginTop: 4, textAlign: 'right' }}>
                        {editedBody.length} / 1600 characters
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      background: C.bgPrimary, borderRadius: 16, padding: 16,
                      border: `1px solid ${C.border}`
                    }}>
                      <div style={{ fontSize: 14, color: C.textPrimary, whiteSpace: 'pre-wrap' }}>
                        {editedBody.replace(/\{\{first_name\}\}/g, previewGuest?.first_name || '')}
                      </div>
                    </div>
                  )}
                  <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: C.textSecondary }}>
                    SMS Preview • {editedBody?.length || 0} characters
                  </div>
                </div>
              )}
            </div>

            {/* Preview Actions */}
            <div style={{
              padding: '16px 20px', borderTop: `1px solid ${C.border}`,
              display: 'flex', gap: 12, justifyContent: 'flex-end'
            }}>
              <button
                onClick={closePreview}
                style={{
                  padding: '10px 20px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: C.bgCard,
                  cursor: 'pointer', fontSize: 14, color: C.textPrimary
                }}
              >
                Close
              </button>
              <button
                data-testid="preview-confirm-send-btn"
                onClick={() => { closePreview(); handleSend(); }}
                style={{
                  padding: '10px 20px', borderRadius: 8,
                  border: 'none', background: C.gold, color: '#000',
                  cursor: 'pointer', fontSize: 14, fontFamily: 'Cinzel, serif'
                }}
              >
                Confirm & Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Send Confirmation Modal */}
      {showBulkConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: C.bgCard, borderRadius: 12, padding: 24,
            maxWidth: 450, width: '90%', border: `1px solid ${C.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <AlertTriangle size={24} color={C.warning} />
              <h3 style={{ margin: 0, fontFamily: 'Cinzel, serif', color: C.textPrimary }}>
                Confirm Bulk Send
              </h3>
            </div>

            <p style={{ fontSize: 14, color: C.textPrimary, marginBottom: 8 }}>
              You are about to send to <strong>{selectedGuests.length} recipients</strong>.
            </p>
            
            <p style={{ fontSize: 13, color: C.textSecondary, marginBottom: 16 }}>
              First 3 recipients: {guestList.slice(0, 3).map(g => g.email || g.first_name).join(', ')}
              {guestList.length > 3 && '...'}
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.textPrimary, display: 'block', marginBottom: 8 }}>
                Type <strong>SEND</strong> to confirm:
              </label>
              <input
                data-testid="bulk-confirm-input"
                value={bulkConfirmText}
                onChange={e => setBulkConfirmText(e.target.value)}
                placeholder="Type SEND"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 6,
                  border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box',
                  background: C.bgInput, color: C.textPrimary
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowBulkConfirm(false); setBulkConfirmText(''); }}
                style={{
                  padding: '10px 20px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: C.bgCard,
                  cursor: 'pointer', fontSize: 14, color: C.textPrimary
                }}
              >
                Cancel
              </button>
              <button
                data-testid="bulk-confirm-send-btn"
                onClick={() => { if (bulkConfirmText === 'SEND') executeSend(); }}
                disabled={bulkConfirmText !== 'SEND'}
                style={{
                  padding: '10px 20px', borderRadius: 8,
                  border: 'none',
                  background: bulkConfirmText === 'SEND' ? C.error : C.bgPrimary,
                  color: bulkConfirmText === 'SEND' ? '#fff' : C.textSecondary,
                  cursor: bulkConfirmText === 'SEND' ? 'pointer' : 'not-allowed',
                  fontSize: 14, fontFamily: 'Cinzel, serif'
                }}
              >
                Send to {selectedGuests.length} Recipients
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Template Preview Modal */}
      {showQuickPreview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: C.bgCard, borderRadius: 12, width: '90%', maxWidth: 700,
            maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            border: `1px solid ${C.border}`
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontFamily: 'Cinzel, serif', color: C.textPrimary }}>
                Template Preview: {quickPreviewContent.name}
              </h3>
              <button
                data-testid="close-quick-preview"
                onClick={() => setShowQuickPreview(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: C.textSecondary }}
              >
                ×
              </button>
            </div>

            {/* Subject (Email only) */}
            {mode === 'email' && quickPreviewContent.subject && (
              <div style={{ padding: '12px 20px', background: C.bgPrimary, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 13, color: C.textSecondary }}>
                  <strong>Subject:</strong> {quickPreviewContent.subject}
                </div>
              </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: 20, background: C.bgPrimary }}>
              {mode === 'email' ? (
                <div style={{
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  background: C.bgCard, minHeight: 400
                }}>
                  <iframe
                    title="Quick Template Preview"
                    srcDoc={quickPreviewContent.body}
                    style={{ width: '100%', height: 450, border: 'none' }}
                  />
                </div>
              ) : (
                <div style={{
                  maxWidth: 320, margin: '0 auto', padding: 20,
                  background: C.bgCard, borderRadius: 20, border: `1px solid ${C.border}`
                }}>
                  <div style={{
                    background: C.bgPrimary, borderRadius: 16, padding: 16,
                    border: `1px solid ${C.border}`
                  }}>
                    <div style={{ fontSize: 14, color: C.textPrimary, whiteSpace: 'pre-wrap' }}>
                      {quickPreviewContent.body}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: C.textSecondary }}>
                    SMS Preview • {quickPreviewContent.body?.length || 0} characters
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 20px', borderTop: `1px solid ${C.border}`,
              display: 'flex', justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowQuickPreview(false)}
                style={{
                  padding: '10px 24px', borderRadius: 8,
                  border: 'none', background: C.gold, color: '#000',
                  cursor: 'pointer', fontSize: 14, fontFamily: 'Cinzel, serif'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendNoticesTab;
